<template>
	<div v-if="deviceModelForm" class="py-8">
		<DeviceModelPropertiesForm :form="deviceModelForm" @on-save="saveDeviceModel" @on-delete="confirmDelete" :disabled="isReadOnly"/>
	</div>
</template>

<script>
import DeviceModelPropertiesForm from "@/Forms/DeviceModel/DeviceModelPropertiesForm.vue";
import { mapStores } from "pinia";
import { useDeviceModelsStore } from "@/stores/deviceModels.js";

export default {
	name: "DeviceModelPropertiesView",
	components: {DeviceModelPropertiesForm},
	data() {
		return {
			deviceModelForm: undefined,
			readOnly: false,
		}
	},
	emits: [
		'on-save',
		'on-delete',
	],
	props: {
		model: {
			type: Object,
		},
	},
	computed: {
		...mapStores(useDeviceModelsStore),
		isReadOnly() {
			return this.readOnly;
		},
	},
	methods: {
		asReadOnlyForm(data) {
			return {
				...data,
				errors: {},
				processing: false,
				validate: () => {},
			};
		},
		async loadDeviceModelForm(modelId) {
			this.deviceModelForm = undefined;
			this.readOnly = false;

			if (!modelId) {
				return;
			}

			if (modelId === 'NEW') {
				this.deviceModelForm = await this.deviceModelsStore.createDeviceModel();
				return;
			}

			const deviceModel = this.model ?? await this.deviceModelsStore.showDeviceModel(modelId);
			if (this.$route.params.model !== modelId) {
				return;
			}

			if (this.isGlobalModel(deviceModel) && !this.$auth.hasPermission('update:all-device-models')) {
				this.readOnly = true;
				this.deviceModelForm = this.asReadOnlyForm(deviceModel);
				return;
			}

			this.deviceModelForm = await this.deviceModelsStore.editDeviceModel(modelId);
			if (this.$route.params.model !== modelId) {
				return;
			}
			this.deviceModelForm.id = modelId;
		},
		async saveDeviceModel(pendingImageFile = null) {
			console.log('saving device model');
			try {
				const response = await this.deviceModelForm.submit();
				const recordId = response.data.id;
				this.$toast.add({
					severity: 'success',
					summary: this.$t('pages.admin.devices.device_models.device_model.properties.device_model_properties_view.device_model_saved'),
					life: 3000,
				});
				if (pendingImageFile) {
					try {
						const formData = new FormData();
						formData.append('image', pendingImageFile);
						const imageResponse = await axios.post(`device-models/${recordId}/image`, formData, {
							headers: { 'Content-Type': 'multipart/form-data' },
						});
						if (this.deviceModelForm) {
							this.deviceModelForm.image_url = imageResponse.data.image_url;
						}
						this.$toast.add({
							severity: 'success',
							summary: this.$t('pages.admin.devices.device_models.device_model.properties.device_model_properties_view.image_upload_success'),
							life: 3000,
						});
					} catch (imageError) {
						console.error('Image upload failed', imageError);
						this.$toast.add({
							severity: 'error',
							summary: this.$t('pages.admin.devices.device_models.device_model.properties.device_model_properties_view.image_upload_failed'),
							life: 3000,
						});
					}
				}
				this.$emit('on-save');
				this.$router.replace({params: {...this.$route.params, model: recordId}});
			} catch (error) {
				//
			}
		},
		confirmDelete() {
			this.$confirm.require({
				group: 'delete',
        message: this.$t('pages.admin.devices.device_models.device_model.properties.device_model_properties_view.are_you_sure_you_want_to_delete_this_device_model'),
        header: this.$t('pages.admin.devices.device_models.device_model.properties.device_model_properties_view.delete_device_model'),
        accept: () => {
					this.deleteDeviceModel();
				},
				reject: () => {
					this.$toast.add({
            severity: 'info',
            summary: this.$t('common.cancelled'),
            detail: this.$t('pages.admin.devices.device_models.device_model.properties.device_model_properties_view.device_model_was_not_deleted'),
            life: 3000 });
				}
			});
		},
		async deleteDeviceModel() {
			try {
				if (this.$route.params.model !== 'NEW') {
					await this.deviceModelsStore.deleteDeviceModel(this.$route.params.model);
					this.$emit('on-delete');
				}
				this.$router.push({name: 'admin.devices.models', params: {...this.$route.params, model: undefined}});
			} catch (error) {
				console.log(error);
			}
		},
    isGlobalModel(deviceModel) {
      return deviceModel?.company_id == null
          && !deviceModel?.company_ids?.length
          && !deviceModel?.companies?.length;
    },
	},
	watch: {
		'$route.params.model': {
			async handler(newValue, oldValue) {
				await this.loadDeviceModelForm(newValue);
			},
			immediate: true,
		}
	},
}
</script>

<style scoped>

</style>
